import React, { useState, useEffect } from 'react';
import { FaGraduationCap, FaArrowUp, FaArrowDown, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import API_URL from '../config';
import DrillDown from '../DrillDown';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

function Education() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('literacy');
  const [drillDown, setDrillDown] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/education`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success') {
          setData(result.data.sort((a, b) => a.year - b.year));
        } else {
          setError(result.message);
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to connect to API');
        setLoading(false);
      });
  }, []);

  const latest = data.length > 0 ? data[data.length - 1] : null;
  const previous = data.length > 1 ? data[data.length - 2] : null;

  const METRICS = [
    {
      title: "Literacy Rate",
      value: `${latest?.literacy_rate}%`,
      change: previous ? `${(latest?.literacy_rate - previous?.literacy_rate).toFixed(1)}% vs last year` : null,
      isPositive: latest?.literacy_rate > previous?.literacy_rate,
      subtitle: "Adults 15+",
      metric: {
        key: 'literacy_rate',
        label: 'Literacy Rate',
        color: '#8B5CF6',
        unit: '%',
        higherIsBetter: true,
        description: 'Percentage of adults aged 15 and above who can read and write',
        info: 'Kenya has one of the highest literacy rates in Sub-Saharan Africa, driven by free primary education introduced in 2003 and ongoing investments in education.'
      }
    },
    {
      title: "Primary Enrollment",
      value: `${latest?.primary_enrollment}%`,
      change: previous ? `${(latest?.primary_enrollment - previous?.primary_enrollment).toFixed(1)}% vs last year` : null,
      isPositive: latest?.primary_enrollment > previous?.primary_enrollment,
      subtitle: "Gross rate",
      metric: {
        key: 'primary_enrollment',
        label: 'Primary School Enrollment',
        color: '#4F46E5',
        unit: '%',
        higherIsBetter: true,
        description: 'Gross enrollment ratio for primary education',
        info: 'Kenya achieved near-universal primary education enrollment following the introduction of Free Primary Education (FPE) in 2003, which saw enrollment jump dramatically.'
      }
    },
    {
      title: "Secondary Enrollment",
      value: `${latest?.secondary_enrollment}%`,
      change: previous ? `${(latest?.secondary_enrollment - previous?.secondary_enrollment).toFixed(1)}% vs last year` : null,
      isPositive: latest?.secondary_enrollment > previous?.secondary_enrollment,
      subtitle: "Net rate",
      metric: {
        key: 'secondary_enrollment',
        label: 'Secondary School Enrollment',
        color: '#10B981',
        unit: '%',
        higherIsBetter: true,
        description: 'Net enrollment ratio for secondary education',
        info: 'Secondary enrollment has improved significantly following the introduction of Free Day Secondary Education (FDSE) in 2008, though dropout rates remain a challenge.'
      }
    },
    {
      title: "Tertiary Enrollment",
      value: `${latest?.tertiary_enrollment}%`,
      change: previous ? `${(latest?.tertiary_enrollment - previous?.tertiary_enrollment).toFixed(1)}% vs last year` : null,
      isPositive: latest?.tertiary_enrollment > previous?.tertiary_enrollment,
      subtitle: "Gross rate",
      metric: {
        key: 'tertiary_enrollment',
        label: 'Tertiary Education Enrollment',
        color: '#EC4899',
        unit: '%',
        higherIsBetter: true,
        description: 'Gross enrollment ratio for tertiary education',
        info: 'Tertiary enrollment in Kenya has been growing with the expansion of universities and TVETs, though it remains below the Sub-Saharan Africa average.'
      }
    }
  ];

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <FaSpinner className="text-5xl text-purple-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading education data...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center bg-white p-8 rounded-lg shadow-md">
        <p className="text-red-600 text-xl font-semibold mb-2">Error loading data</p>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-purple-700 to-purple-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center mb-4">
              <FaGraduationCap className="text-4xl mr-4" />
              <div>
                <h1 className="text-4xl font-bold">Education Statistics</h1>
                <p className="text-purple-200 mt-1">Kenya Literacy, Enrollment & Learning Trends</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Key Metrics */}
        {latest && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {METRICS.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <MetricCard
                  {...item}
                  onClick={() => setDrillDown(item.metric)}
                />
              </motion.div>
            ))}
          </div>
        )}

        {/* Chart Tabs */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Education Trends</h3>
            <div className="flex gap-2">
              {[
                { key: 'literacy', label: 'Literacy' },
                { key: 'enrollment', label: 'Enrollment' },
                { key: 'comparison', label: 'All Levels' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveChart(tab.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activeChart === tab.key
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Literacy Area Chart */}
          {activeChart === 'literacy' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="literacyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value}%`, 'Literacy Rate']}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="literacy_rate"
                    stroke="#8B5CF6"
                    strokeWidth={3}
                    fill="url(#literacyGradient)"
                    dot={{ fill: '#8B5CF6', r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Literacy Rate %"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Enrollment Bar Chart */}
          {activeChart === 'enrollment' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value}%`, '']}
                  />
                  <Legend />
                  <Bar
                    dataKey="primary_enrollment"
                    fill="#8B5CF6"
                    name="Primary %"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="secondary_enrollment"
                    fill="#EC4899"
                    name="Secondary %"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="tertiary_enrollment"
                    fill="#10B981"
                    name="Tertiary %"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* All Levels Line Chart */}
          {activeChart === 'comparison' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value}%`, '']}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="literacy_rate"
                    stroke="#8B5CF6"
                    strokeWidth={2}
                    dot={{ fill: '#8B5CF6', r: 4 }}
                    activeDot={{ r: 7 }}
                    name="Literacy %"
                  />
                  <Line
                    type="monotone"
                    dataKey="primary_enrollment"
                    stroke="#4F46E5"
                    strokeWidth={2}
                    dot={{ fill: '#4F46E5', r: 4 }}
                    activeDot={{ r: 7 }}
                    name="Primary Enrollment %"
                  />
                  <Line
                    type="monotone"
                    dataKey="secondary_enrollment"
                    stroke="#EC4899"
                    strokeWidth={2}
                    dot={{ fill: '#EC4899', r: 4 }}
                    activeDot={{ r: 7 }}
                    name="Secondary Enrollment %"
                  />
                  <Line
                    type="monotone"
                    dataKey="tertiary_enrollment"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ fill: '#10B981', r: 4 }}
                    activeDot={{ r: 7 }}
                    name="Tertiary Enrollment %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
          )}
        </div>

        {/* Historical Data Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Historical Data</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Year</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Literacy %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Primary %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Secondary %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Tertiary %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {[...data].reverse().map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="border-t hover:bg-purple-50 transition cursor-pointer"
                  >
                    <td className="px-4 py-3 font-semibold text-purple-600">{row.year}</td>
                    <td className="px-4 py-3">{row.literacy_rate}%</td>
                    <td className="px-4 py-3">{row.primary_enrollment}%</td>
                    <td className="px-4 py-3">{row.secondary_enrollment}%</td>
                    <td className="px-4 py-3">{row.tertiary_enrollment}%</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{row.source}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Data Source */}
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <p className="text-sm text-gray-700">
            <strong>Data Sources:</strong> Ministry of Education Kenya,
            Kenya National Examinations Council (KNEC), UNESCO
          </p>
        </div>
      </div>

      {/* DrillDown Panel */}
      <DrillDown
        isOpen={drillDown !== null}
        onClose={() => setDrillDown(null)}
        metric={drillDown}
        data={data}
      />
    </div>
  );
}

function MetricCard({ title, value, change, isPositive, subtitle, onClick }) {
  return (
    <div
      className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition cursor-pointer hover:scale-105 transform"
      onClick={onClick}
    >
      <p className="text-gray-600 text-sm mb-2">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
      <div className="flex items-center">
        {isPositive !== null && (
          isPositive ?
            <FaArrowUp className="text-green-600 mr-1" /> :
            <FaArrowDown className="text-red-600 mr-1" />
        )}
        <p className={`text-sm ${isPositive === true ? 'text-green-600' : isPositive === false ? 'text-red-600' : 'text-gray-600'}`}>
          {change}
        </p>
      </div>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
      <p className="text-xs text-indigo-500 mt-2 font-medium">Click for details →</p>
    </div>
  );
}

export default Education;